import { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TestBadge } from "@/components/common";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight } from "lucide-react";

const LAYERS = [
  { key: "consultations", label: "Consultations", cls: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]" },
  { key: "tasks", label: "Tasks", cls: "bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))]" },
  { key: "followups", label: "Follow-ups", cls: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" },
  { key: "cases", label: "Case deadlines", cls: "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]" },
  { key: "holidays", label: "Holidays", cls: "bg-[hsl(var(--test))]/15 text-[hsl(var(--test))]" },
];

const istDate = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const istTime = (d) => new Date(d).toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", hour: "2-digit", minute: "2-digit" });

export default function CalendarPage() {
  const navigate = useNavigate();
  const { hasCap } = useAuth();
  const [anchor, setAnchor] = useState(new Date());
  const [view, setView] = useState("month");
  const [layers, setLayers] = useState(LAYERS.map((l) => l.key));
  const [events, setEvents] = useState(null);
  // Leadership team/company view (2026-09 Phase 2) — same reports.team/
  // reports.company gate the backend already enforces. Google Calendar
  // itself is one shared organisation-wide calendar (no per-employee
  // credentials exist) — this filters SalesOS's own appointment/task/case
  // records by assigned employee, which is the honest limit of what's
  // possible until real per-employee Google Calendar sync exists.
  const canScopeTeam = hasCap("reports.team") || hasCap("reports.company");
  const [scope, setScope] = useState("own");
  const [scopeEmployee, setScopeEmployee] = useState("all");
  const [employees, setEmployees] = useState([]);
  useEffect(() => {
    if (canScopeTeam) api.get("/users").then((r) => setEmployees((r.data.users || []).filter((u) => u.is_active))).catch(() => {});
  }, [canScopeTeam]);

  const range = useMemo(() => {
    const a = new Date(anchor);
    if (view === "month") {
      const start = new Date(a.getFullYear(), a.getMonth(), 1);
      const end = new Date(a.getFullYear(), a.getMonth() + 1, 1);
      return { start, end };
    }
    if (view === "week") {
      const day = (a.getDay() + 6) % 7;
      const start = new Date(a); start.setDate(a.getDate() - day); start.setHours(0, 0, 0, 0);
      const end = new Date(start); end.setDate(start.getDate() + 7);
      return { start, end };
    }
    const start = new Date(a); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(start.getDate() + 1);
    return { start, end };
  }, [anchor, view]);

  const load = useCallback(() => {
    setEvents(null);
    const params = new URLSearchParams({
      start: range.start.toISOString(), end: range.end.toISOString(),
      layers: layers.join(","),
    });
    if (canScopeTeam && scope !== "own") {
      params.set("scope", scope);
      if (scopeEmployee !== "all") params.set("owner_user_id", scopeEmployee);
    }
    api.get(`/calendar?${params.toString()}`).then((r) => setEvents(r.data));
  }, [range, layers, scope, scopeEmployee, canScopeTeam]);
  useEffect(() => { load(); }, [load]);

  const byDay = useMemo(() => {
    const m = {};
    (events || []).forEach((e) => { const k = istDate(e.at); (m[k] = m[k] || []).push(e); });
    return m;
  }, [events]);

  const step = (dir) => {
    const a = new Date(anchor);
    if (view === "month") a.setMonth(a.getMonth() + dir);
    else a.setDate(a.getDate() + dir * (view === "week" ? 7 : 1));
    setAnchor(a);
  };

  const monthCells = useMemo(() => {
    if (view !== "month") return [];
    const first = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
    const startPad = (first.getDay() + 6) % 7;
    const daysInMonth = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(anchor.getFullYear(), anchor.getMonth(), d));
    return cells;
  }, [anchor, view]);

  const layerMeta = Object.fromEntries(LAYERS.map((l) => [l.key, l]));

  return (
    <div className="space-y-4" data-testid="calendar-page">
      <div className="flex items-center gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{scope === "own" ? "My Calendar" : "Team Calendar"}</h1>
          <p className="text-sm text-muted-foreground">
            {scope === "own" ? "Your own consultations, tasks and case deadlines. " : "Every assigned employee's consultations, tasks and case deadlines. "}
            SalesOS stays canonical; Google Calendar is a single shared organisation calendar that mirrors it when connected — true per-employee Google sync doesn't exist yet.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button data-testid="calendar-prev" variant="outline" size="icon" className="h-9 w-9" onClick={() => step(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <span className="text-sm font-medium min-w-36 text-center" data-testid="calendar-title">
            {view === "month"
              ? anchor.toLocaleDateString("en-IN", { month: "long", year: "numeric" })
              : anchor.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </span>
          <Button data-testid="calendar-next" variant="outline" size="icon" className="h-9 w-9" onClick={() => step(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Tabs value={view} onValueChange={setView}>
            <TabsList className="h-9">
              <TabsTrigger data-testid="calendar-view-day" value="day">Day</TabsTrigger>
              <TabsTrigger data-testid="calendar-view-week" value="week">Week</TabsTrigger>
              <TabsTrigger data-testid="calendar-view-month" value="month">Month</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>
      {canScopeTeam && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={scope} onValueChange={(v) => { setScope(v); setScopeEmployee("all"); }}>
            <SelectTrigger data-testid="calendar-scope-select" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="own">My calendar</SelectItem>
              {hasCap("reports.team") && <SelectItem value="team">Team</SelectItem>}
              {hasCap("reports.company") && <SelectItem value="company">All</SelectItem>}
            </SelectContent>
          </Select>
          {scope !== "own" && (
            <Select value={scopeEmployee} onValueChange={setScopeEmployee}>
              <SelectTrigger data-testid="calendar-scope-employee" className="h-8 w-44 text-xs"><SelectValue placeholder="Employee…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Every employee</SelectItem>
                {employees.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>
      )}
      <div className="flex items-center gap-2 flex-wrap">
        {LAYERS.map((l) => (
          <button key={l.key} data-testid={`calendar-layer-${l.key}`}
            onClick={() => setLayers((cur) => cur.includes(l.key) ? cur.filter((k) => k !== l.key) : [...cur, l.key])}
            className={cn("text-xs px-2.5 py-1 rounded-full border transition-colors",
              layers.includes(l.key) ? l.cls + " border-transparent font-medium" : "text-muted-foreground bg-muted/40")}>
            {l.label}
          </button>
        ))}
      </div>

      {!events ? <Skeleton className="h-96" /> : view === "month" ? (
        <Card><CardContent className="p-3">
          <div className="grid grid-cols-7 text-[11px] text-muted-foreground font-semibold mb-1">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="calendar-month-grid">
            {monthCells.map((d, i) => (
              <div key={i} className={cn("min-h-24 rounded-md border p-1", !d && "bg-muted/30 border-transparent")}>
                {d && (
                  <>
                    <span className={cn("text-[11px] font-medium", istDate(new Date()) === istDate(d) && "text-primary font-bold")}>{d.getDate()}</span>
                    <div className="space-y-0.5 mt-0.5">
                      {(byDay[istDate(d)] || []).slice(0, 3).map((e, j) => (
                        <button key={j} onClick={() => e.link && navigate(e.link)}
                          className={cn("w-full text-left text-[10px] px-1 py-0.5 rounded truncate block", layerMeta[e.layer]?.cls)}>
                          {e.layer === "holidays" ? "" : istTime(e.at) + " "}{e.title}
                        </button>
                      ))}
                      {(byDay[istDate(d)] || []).length > 3 && (
                        <span className="text-[10px] text-muted-foreground px-1">+{byDay[istDate(d)].length - 3} more</span>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </CardContent></Card>
      ) : (
        <Card><CardContent className="p-4 space-y-2" data-testid="calendar-list">
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">Nothing scheduled in this period.</p>
          ) : events.map((e, i) => (
            <button key={i} onClick={() => e.link && navigate(e.link)}
              className="w-full flex items-center gap-3 p-2 rounded-md border text-left hover:bg-muted/50">
              <span className="text-xs text-muted-foreground w-28 shrink-0">{istDate(e.at)} {istTime(e.at)}</span>
              <Badge variant="outline" className={cn("text-[10px] border-transparent", layerMeta[e.layer]?.cls)}>{layerMeta[e.layer]?.label}</Badge>
              <span className="text-sm flex-1 truncate">{e.title}</span>
              <TestBadge show={e.is_test} />
            </button>
          ))}
        </CardContent></Card>
      )}
    </div>
  );
}
